import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SelectMenu from "../../controls/SelectMenu";
import Button from "../../ui/Button";
import { makePostApiCall } from "../../../shared/makeApiCall";

const formatCases = (value) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount.toLocaleString() : "0";
};

const getErrorMessage = (result, fallback) =>
  result?.data?.message ??
  result?.data?.shipment_creation_reason ??
  result?.data?.subtitle ??
  fallback;

export default function CreateShipmentModal({ setOpen, logout }) {
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const close = () => setOpen({ open: false, childComponent: null });

  useEffect(() => {
    let active = true;

    const loadOrders = async () => {
      const result = await makePostApiCall("getOpenOrdersForDropdown", {});
      if (!active) return;

      if (result.success) {
        setOrders(Array.isArray(result.data) ? result.data : []);
      } else {
        if (result.errorStatus === 401) logout();
        setError(getErrorMessage(result, "Unable to load available orders."));
      }
      setLoadingOrders(false);
    };

    loadOrders();
    return () => {
      active = false;
    };
  }, [logout]);

  const selectOrder = async (orderId) => {
    setSelectedOrderId(orderId);
    setOrder(null);
    setError("");
    if (!orderId) return;

    setLoadingOrder(true);
    const result = await makePostApiCall(`fetchOrder/${orderId}`, {});
    if (result.success) {
      setOrder(result.data);
    } else {
      if (result.errorStatus === 401) logout();
      setError(getErrorMessage(result, "Unable to load this order."));
    }
    setLoadingOrder(false);
  };

  const createShipment = async () => {
    if (!order?.id || !order.can_create_shipment) return;

    setCreating(true);
    setError("");
    const result = await makePostApiCall(`orders/${order.id}/shipments`, {
      created_by: order.created_by ?? null,
      notes: null,
    });

    if (result.success) {
      const shippingId = result.data?.id ?? result.data?.shipping?.id;
      if (shippingId) {
        window.location.assign(`/shippings/${shippingId}`);
        return;
      }
      setError(
        "Shipment was created, but its shipment number was not returned.",
      );
    } else {
      if (result.errorStatus === 401) logout();
      setError(getErrorMessage(result, "Unable to create this shipment."));
    }
    setCreating(false);
  };

  const customer = order?.customer?.label ?? order?.customer ?? "—";
  const products = order?.products ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Create Shipment
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Select an eligible order and confirm its items before creating the
            shipment.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-5">
        <SelectMenu
          name="shipmentOrder"
          title="Order"
          options={orders}
          value={selectedOrderId}
          onValueChange={selectOrder}
          placeholder={
            loadingOrders ? "Loading orders..." : "Search by customer or PO #"
          }
          disabled={loadingOrders || creating}
          filter
        />
        {!loadingOrders && orders.length === 0 && !error ? (
          <p className="mt-2 text-sm text-slate-500">
            There are no orders currently eligible for a new shipment.
          </p>
        ) : null}
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loadingOrder ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          Loading order details...
        </div>
      ) : null}

      {order ? (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                PO #
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {order.po_number || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Customer
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {customer}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Ordered
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {formatCases(order.ordered_cases)} cases
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Remaining
              </p>
              <p className="mt-1 text-sm font-semibold text-main-purple">
                {formatCases(order.remaining_cases)} cases
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="border-b border-slate-200 bg-white px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900">
                Order items
              </h3>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">
                      Product
                    </th>
                    <th className="px-4 py-2.5 text-left font-semibold">PLU</th>
                    <th className="px-4 py-2.5 text-right font-semibold">
                      Ordered
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {products.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {item.product_name}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.plu || "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">
                        {formatCases(item.quantity)}{" "}
                        {item.selling_unit_of_measure || "cases"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {!order.can_create_shipment ? (
            <p className="text-sm font-medium text-red-700">
              {order.shipment_creation_reason ||
                "This order is not eligible for a new shipment."}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 flex justify-end gap-2 border-t border-slate-200 pt-4">
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={close}
          disabled={creating}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={createShipment}
          disabled={!order?.can_create_shipment}
          loading={creating}
        >
          Create Shipment
        </Button>
      </div>
    </div>
  );
}
